import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';

const INDUSTRIES = [
  'HVAC', 'Plumbing', 'Electrical', 'Roofing', 'Solar', 'Painting',
  'Flooring', 'Landscaping', 'Tree Service', 'Pool Construction',
  'Pest Control', 'Catering', 'Interior Design', 'Auto Detailing',
  'Auto Body', 'Videography', 'Wedding Photography', 'Web Developer',
  'MSP / IT', 'Manufacturing', 'Signage', 'Handyman', 'Cabinet',
  'Window & Door', 'Construction', 'Other',
];

const RebuildMyQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    businessName: '',
    industry: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    analytics.trackPageView('Rebuild My Quote');
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/leads/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'rebuild-my-quote' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Submission failed');
      analytics.track('Lead Submitted', { source: 'rebuild-my-quote', industry: formData.industry });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again in a sec.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <SEOPageLayout
        title="Quote rebuild request received | QuoteTier"
        description="Your quote rebuild request is in. Lawrence will reply personally within 24 hours."
        canonical="/rebuild-my-quote"
      >
        <section className="py-32 md:py-40">
          <div className="container max-w-2xl mx-auto px-4 text-center">
            <h1 className="font-instrument text-5xl md:text-6xl tracking-tight leading-tight mb-6">
              Got it.
            </h1>
            <p className="font-jetbrains text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
              Your quote rebuild request is in. I'll reply personally from <span className="font-bold text-black">hello@quotetier.com</span> within 24 hours — usually faster.
            </p>
            <p className="font-jetbrains text-sm text-gray-500 mb-10">
              If nothing hits your inbox in a day, check spam (new domain, Gmail is occasionally paranoid) — or reply to the address above.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <Button className="px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg">
                  Start your free account →
                </Button>
              </Link>
              <Link to="/roi">
                <Button className="px-8 py-4 text-lg font-semibold bg-white text-black border border-black hover:bg-gray-50 rounded-lg">
                  See the ROI math
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </SEOPageLayout>
    );
  }

  return (
    <SEOPageLayout
      title="Post your current quote, I'll rebuild it free | QuoteTier"
      description="Paste your current flat-rate quote. Lawrence, founder of QuoteTier, will personally rebuild it as a Good/Better/Best tiered version within 24 hours. Free, no strings."
      canonical="/rebuild-my-quote"
      ogTitle="I'll rebuild your current quote as Good/Better/Best — free"
      ogDescription="Solo-founder offer: send me your flat-rate quote, I'll send back a tiered version in 24 hours. Free forever plan means you can steal the result."
    >
      <section className="py-24 md:py-32 lg:py-40">
        <div className="container max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Post your current quote. <span className="text-primary font-bold">I'll rebuild it free.</span>
            </h1>
            <p className="font-jetbrains text-lg md:text-xl text-gray-600 tracking-tight leading-relaxed max-w-2xl mx-auto">
              Paste your flat-rate quote below (or just describe your current setup). I'll reply personally within 24 hours with a Good/Better/Best tiered version ready to send. You keep it either way.
            </p>
          </div>

          <div className="max-w-xl mx-auto mb-8 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
            <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
              <span className="font-bold text-black">Why I'm doing this:</span> I'm a solo founder. Every rebuild teaches me how contractors actually price, which makes QuoteTier better. In return, you get a tiered quote you can copy straight into your next job — often worth a few thousand in margin on the first sale.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 max-w-xl mx-auto">
            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Your name</span>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base focus:outline-none focus:ring-2 focus:ring-black"
                />
              </label>
              <label className="block">
                <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Email</span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength={200}
                  className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base focus:outline-none focus:ring-2 focus:ring-black"
                />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Business name</span>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  maxLength={200}
                  className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base focus:outline-none focus:ring-2 focus:ring-black"
                />
              </label>
              <label className="block">
                <span className="font-jetbrains text-sm text-gray-600 mb-2 block">Industry</span>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-base focus:outline-none focus:ring-2 focus:ring-black bg-white"
                >
                  <option value="">Select one</option>
                  {INDUSTRIES.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="font-jetbrains text-sm text-gray-600 mb-2 block">
                Paste your current quote — or describe your setup
              </span>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={8}
                maxLength={5000}
                placeholder="Paste your quote here, or tell me: What do you currently send customers? Flat rate? Multiple options? What's the biggest thing you wish your quotes did better?"
                className="w-full px-4 py-3 border border-black rounded-lg font-jetbrains text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-black"
              />
            </label>

            {error && (
              <div className="p-3 border border-red-300 bg-red-50 rounded-lg text-red-700 font-jetbrains text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 disabled:opacity-60 rounded-lg shadow-lg font-instrument"
            >
              {submitting ? 'Sending…' : 'Send it — rebuild my quote →'}
            </button>
            <p className="font-jetbrains text-xs text-gray-500 text-center">
              Replies come from hello@quotetier.com within 24 hours. No list signup, no marketing emails — just the rebuild.
            </p>
          </form>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default RebuildMyQuote;
